import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  LOCALE_ID,
} from '@angular/core';
import {
  PreloadAllModules,
  provideRouter,
  RouteReuseStrategy,
  withPreloading,
  withRouterConfig,
} from '@angular/router';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  IonicRouteStrategy,
  ModalController,
  provideIonicAngular,
} from '@ionic/angular/standalone';
import { TranslateModule } from '@ngx-translate/core';
import { TruncateModule } from '@yellowspot/ng-truncate';
import { CurrencyPipe, DecimalPipe } from '@angular/common';
import { ROUTES } from './app.routes';
import { Interceptor } from './interceptors/interceptor';
import { JwtInterceptor } from './interceptors/jwt.interceptor';
import { NetworkInterceptor } from './interceptors/network.interceptor';
import { PreviewInterceptor } from './interceptors/preview.interceptor';
import { ApiService } from './services/api.service';
import { LoginService } from './services/login.service';
import { ApiInit } from './utils/api-init';
import { StorageService } from './services/storage.service';
import { CurrentWhiteLabelApplication } from './utils/current-white-label-application';
import { ProjectService } from './services/project.service';
import { FeedService } from './services/feed.service';
import { NotificationService } from './services/notification.service';
import { DeviceService } from './services/device.service';
import { OnboardingService } from './services/onboarding.service';
import { ImageService } from './services/image.service';
import { LocationService } from './services/location.service';
import { SettingsService } from './services/settings.service';
import { UpdateService } from './services/update.service';
import { EventService } from './services/event.service';
import { ToastService } from './services/toast.service';
import { NetworkService } from './services/network.service';
import { CacheService } from './services/cache.service';
import { StatisticsService } from './services/statistics.service';
import { TargetService } from './services/target.service';
import { RatingService } from './services/rating.service';
import { LiveStreamService } from './services/live-stream.service';
import { UploadService } from './services/upload.service';
import { ErrorService } from './services/error.service';
import { FileService } from './services/file.service';
import { DeepLinkService } from './services/deep-link.service';
import { PushService } from './services/push.service';
import { PollService } from './services/poll.service';
import { ProjectDocumentService } from './services/project-document.service';
import { ContentTextPipe } from './pipes/content-text.pipe';
import { LinkyPipe } from 'ngx-linky';
import { TranslationService } from './services/translation.service';
import { ProjectStore } from './pages/project/project.store';
import { GlobalErrorHandler } from './utils/gloabl-error-handler';
import * as SentryAngular from '@sentry/angular';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';

const prefersDark =
  typeof globalThis !== 'undefined' &&
  'matchMedia' in globalThis &&
  globalThis.matchMedia('(prefers-color-scheme: dark)').matches;

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      ROUTES,
      withPreloading(PreloadAllModules),
      withRouterConfig({ onSameUrlNavigation: 'ignore' })
    ),
    provideHttpClient(withInterceptorsFromDi()),
    provideIonicAngular({ mode: 'ios', useSetInputAPI: true }),
    importProvidersFrom(
      TranslateModule.forRoot(),
      TruncateModule,
      NgxSkeletonLoaderModule.forRoot({
        animation: prefersDark ? 'progress-dark' : 'progress',
        theme: {
          extendsFromRoot: true,
          background: 'var(--skeleton-background)',
        },
      })
    ),
    provideTranslateHttpLoader({
      prefix: './assets/i18n/',
      suffix: '.json',
    }),
    {
      provide: LOCALE_ID,
      deps: [TranslationService],
      useFactory: (translation: TranslationService) => {
        return translation.getAppLanguage();
      },
    },
    {
      provide: RouteReuseStrategy,
      useClass: IonicRouteStrategy,
    },
    { provide: HTTP_INTERCEPTORS, useClass: Interceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: NetworkInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: PreviewInterceptor,
      multi: true,
    },
    ApiService,
    LoginService,
    ApiInit,
    StorageService,
    CurrentWhiteLabelApplication,
    ProjectService,
    FeedService,
    NotificationService,
    DeviceService,
    OnboardingService,
    ImageService,
    LocationService,
    SettingsService,
    UpdateService,
    EventService,
    ToastService,
    NetworkService,
    CacheService,
    StatisticsService,
    TargetService,
    RatingService,
    LiveStreamService,
    UploadService,
    ErrorService,
    FileService,
    DeepLinkService,
    PushService,
    DecimalPipe,
    PollService,
    ContentTextPipe,
    LinkyPipe,
    ProjectDocumentService,
    ModalController,
    CurrencyPipe,
    { provide: ProjectStore, useClass: ProjectStore },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    {
      provide: ErrorHandler,
      useValue: SentryAngular.createErrorHandler({ showDialog: false }),
    },
  ],
};
